/**
 * Los textos de la lectura del DNI, en los tres idiomas.
 *
 * Vivían adentro del diálogo; están acá para que los use también la pantalla
 * pública en pasos. Los tres idiomas tienen que tener las mismas claves: una
 * clave que falta en un idioma se ve vacía en pantalla.
 */
export type IdiomaDni = 'es' | 'en' | 'pt';

export const TEXTOS_DNI: Record<IdiomaDni, Record<string, string>> = {
  es: {
    title: 'Foto del DNI (frente y dorso)',
    intro: 'Con estas fotos se completan los datos del autor y se envía el documento al servidor de SADAIC. Se leen en tu dispositivo: hasta que toques "Enviar a SADAIC" no salen de acá.',
    anverso: 'Frente del DNI',
    reverso: 'Dorso del DNI',
    elegir: 'Elegir foto',
    tomar: 'Tomar foto',
    camaraFrente: 'Sacar foto del frente del DNI',
    camaraDorso: 'Sacar foto del dorso del DNI',
    procesando: 'Leyendo la foto…',
    extrayendo: 'Leyendo los datos del documento…',
    okAnverso: 'Frente verificado',
    cortePregunta: 'Esta foto parece traer las DOS caras del documento. Si es así las separamos y completamos el frente y el dorso de una sola vez. La línea punteada muestra por dónde cortaríamos.',
    corteSi: 'Sí, separar las dos caras', corteNo: 'No, es una sola cara',
    noSalieron: 'No salieron del frente:', loQueVioElOcr: 'Esto es lo que leyó el OCR, renglón por renglón:',
    reconocido: 'Reconocimos en la foto:', rNumero: 'N° de documento', rApellido: 'Apellido', rNombre: 'Nombre', rNacimiento: 'Nacimiento', rNada: 'No pudimos ubicar ningún dato en la foto.',
    rSexo: 'Sexo', rNacionalidad: 'Nacionalidad', rVencimiento: 'Vencimiento',
    okReverso: 'Dorso verificado (número, nombre y fecha coinciden)',
    avisoAlEnviar: 'De la foto del frente no pudimos leer: {faltan}. Se puede enviar igual —SADAIC pide la foto, no que la máquina la lea—: apretá otra vez para mandarla.',
    enviar: 'Enviar a SADAIC',
    cerrar: 'Cerrar',
    enviado: 'DNI enviado correctamente.',
    enviadoDetalle: 'Ya podés cerrar esta ventana.',
    datosLeidos: 'Datos leídos del documento:',
    completar: 'Completar el formulario con estos datos',
    copiar: 'Copiar', copiarDni: 'Copiar DNI',
    fCuitCalculado: 'CUIT/CUIL que le corresponde', calculado: 'calculado',
    completado: 'Se completó el formulario con los datos del DNI. Revisalos antes de confirmar.',
    verificado: 'verificado',
    coinciden: '2 fuentes coinciden',
    enConflicto: 'lecturas diferentes',
    confirmado: 'confirmado en el frente',
    sinVerificar: 'sin verificar',
    arreglaCompletando: 'Si los datos del formulario están vacíos o mal cargados, completalos con los del documento y la verificación se rehace sola.',
    fGenero: 'Género',
    fDocumento: 'N° de documento',
    fVencimiento: 'Vencimiento',
    fNacionalidad: 'Nacionalidad',
    vencido: 'VENCIDO',
    avisoVencido: 'El documento está vencido. Se puede enviar igual: SADAIC pide la foto del DNI, no que esté vigente.',
    tramiteTapado: 'Tapamos el Nº de Trámite y el código de barras, que lo lleva adentro. Los datos del código ya los leímos: lo que se pierde es que alguien más pueda leerlos de la foto.',
    taparAuto: 'Tapar automáticamente',
    taparAutoAyuda: 'Si necesitás que se vea algo de lo tapado, destildalo. También podés tapar lo que quieras arrastrando un recuadro sobre la imagen.',
    fFiscal: 'CUIT/CUIL',
    fDomicilio: 'Domicilio (según el DNI)',
    domicilioNoLeido: 'No se pudo leer del documento. Cargalo a mano.',
    cuitOk: 'El CUIT cargado le corresponde a este documento.',
    cuitSexo: 'El CUIT empieza con un prefijo que no corresponde al sexo del documento: el 20 es de varones y el 27 de mujeres. Puede estar mal el CUIT, el género cargado, o la lectura del documento — el sexo es el único dato del MRZ sin dígito de control.',
    incluirDomicilio: 'Completar también el domicilio',
    domicilioPuedeDiferir: 'El domicilio del DNI puede estar desactualizado. Destildalo si el real es otro.',
    fPaisNacimiento: 'País de nacimiento', fIngreso: 'Fecha de ingreso al país',
    fCategoria: 'Categoría de ingreso', fExpediente: 'Expediente', fDisposicion: 'Disposición',
    fRadicacion: 'Fecha de radicación',
    fCalle: 'Calle y número',
    fLocalidad: 'Localidad',
    fProvincia: 'Provincia',
    fPais: 'País',
    mrz_ilegible: 'No pudimos leer el dorso. Sacá la foto de frente, con buena luz y sin reflejos, con la banda de letras de abajo completa.',
    mrz_no_valida: 'Leímos la banda de letras del dorso, pero sus dígitos de control no cierran. Sacar otra foto no lo va a cambiar: o el documento es una muestra o un espécimen, o hay un dígito mal leído. Se puede enviar igual.',
    dni_mismatch: 'El número de documento de la foto no coincide con los datos cargados.',
    birth_mismatch: 'La fecha de nacimiento de la foto no coincide con los datos cargados.',
    name_mismatch: 'El nombre de la foto no coincide con los datos cargados.',
    anverso_dni: 'No se lee el número de documento en el frente. Probá con mejor luz y sin reflejos.',
    anverso_numero_ajeno: 'La foto se lee bien: el número que dice el documento es {leido} y los datos cargados dicen {suyo}. Lo que no coincide es el documento, no la foto.',
    anverso_apellido: 'No se lee el apellido en el frente. Probá con mejor luz y sin reflejos.',
    anverso_otro_documento: 'El frente y el dorso no son del mismo documento: el número que se lee en el frente no es el del dorso. Revisá que las dos fotos sean del mismo DNI.',
    anverso_sin_referencia: 'Para comprobar el frente hace falta el dorso: cargalo y lo verificamos. También sirve tener los datos cargados en el formulario.',
    calidad: 'A quien reciba esta foto puede costarle leerla:',
    calidadNoBloquea: 'Se puede enviar igual: esto es un aviso, no impide el envío.',
    m_fuera_de_foco: 'está fuera de foco — alejá un poco el documento de la cámara',
    m_reflejo: 'hay un reflejo sobre el plástico — girá el documento para que la luz no le pegue de frente',
    m_muy_oscura: 'está muy oscura — buscá más luz',
    m_muy_chica: 'el documento quedó muy chico en la foto — acercalo hasta que llene el cuadro',
    m_recortada: 'el documento quedó cortado — encuadralo entero',
    ocr_load_failed: 'No se pudo cargar el lector de documentos. Revisá tu conexión y volvé a intentar.',
    upload_failed: 'No se pudo enviar. Volvé a intentar en unos minutos.',
    e_data_mismatch: 'Los datos guardados no coinciden con el documento. Corregilos en el formulario —podés completarlos desde el DNI, acá mismo— y confirmalos antes de enviar la foto.',
    e_mrz_invalid: 'El servidor no pudo leer la banda del dorso. Sacá otra foto, más nítida y con la banda completa.',
    e_bad_image: 'El servidor rechazó las imágenes. Probá con otras fotos (JPG).',
    e_too_large: 'Las fotos son demasiado grandes o demasiado chicas. Sacá otra con la cámara, sin recortarla.',
    e_rate_limited: 'Demasiados intentos seguidos. Esperá unos minutos y volvé a intentar.',
    e_link: 'El enlace de validación ya no es válido. Pedí uno nuevo a quien te envió el email.',
    e_link_expired: 'Este enlace venció. Pedí uno nuevo a quien te envió el email.',
    image_error: 'No se pudo procesar la imagen. Probá con otra foto (JPG).',
  },
  en: {
    title: 'ID card photo (front and back)',
    intro: 'These photos fill in the author\'s data and send the document to the SADAIC server. They are read on your device: until you press "Send to SADAIC" they do not leave it.',
    anverso: 'ID front',
    reverso: 'ID back',
    elegir: 'Choose photo',
    tomar: 'Take photo',
    camaraFrente: 'Take a photo of the ID front',
    camaraDorso: 'Take a photo of the ID back',
    procesando: 'Reading the photo…',
    extrayendo: 'Reading the document data…',
    okAnverso: 'Front verified',
    cortePregunta: 'This photo seems to have BOTH sides of the document. If so, we split it and fill in front and back at once. The dashed line shows where we would cut.',
    corteSi: 'Yes, split the two sides', corteNo: 'No, it is a single side',
    noSalieron: 'Missing from the front:', loQueVioElOcr: 'This is what the OCR read, line by line:',
    reconocido: 'Recognized in the photo:', rNumero: 'ID number', rApellido: 'Last name', rNombre: 'First name', rNacimiento: 'Birth', rNada: 'We could not locate any data in the photo.',
    rSexo: 'Sex', rNacionalidad: 'Nationality', rVencimiento: 'Expiry',
    okReverso: 'Back verified (number, name and date match)',
    avisoAlEnviar: 'From the front photo we could not read: {faltan}. You can send it anyway — SADAIC asks for the photo, not for a machine to read it: press again to send.',
    enviar: 'Send to SADAIC',
    cerrar: 'Close',
    enviado: 'ID sent successfully.',
    enviadoDetalle: 'You can close this window.',
    datosLeidos: 'Data read from the document:',
    completar: 'Fill the form with this data',
    copiar: 'Copy', copiarDni: 'Copy ID number',
    fCuitCalculado: 'Matching tax ID', calculado: 'calculated',
    completado: 'The form was filled with the ID data. Please review it before confirming.',
    verificado: 'verified',
    coinciden: '2 sources agree',
    enConflicto: 'readings differ',
    confirmado: 'confirmed on the front',
    sinVerificar: 'unverified',
    arreglaCompletando: 'If the form data is empty or wrong, fill it from the document and the check is redone automatically.',
    fGenero: 'Gender',
    fDocumento: 'Document number',
    fVencimiento: 'Expiry',
    fNacionalidad: 'Nationality',
    vencido: 'EXPIRED',
    avisoVencido: 'The document is expired. It can be sent anyway: SADAIC asks for the photo of the ID, not for it to be current.',
    tramiteTapado: 'We covered the Trámite number and the barcode, which carries it inside. We already read the barcode data: what is lost is anyone else reading it off the photo.',
    taparAuto: 'Cover automatically',
    taparAutoAyuda: 'If you need something covered to be visible, untick this. You can also cover anything by dragging a rectangle over the image.',
    fFiscal: 'Tax ID',
    fDomicilio: 'Address (per the ID)',
    domicilioNoLeido: 'Could not be read from the document. Enter it manually.',
    cuitOk: 'The tax ID on file matches this document.',
    cuitSexo: 'The tax ID starts with a prefix that does not match the sex on the document: 20 is for men and 27 for women. The tax ID, the gender on file, or the reading of the document may be wrong — sex is the only MRZ field with no check digit.',
    incluirDomicilio: 'Fill in the address too',
    domicilioPuedeDiferir: 'The ID address may be out of date. Uncheck it if the real one is different.',
    fPaisNacimiento: 'Country of birth', fIngreso: 'Date of entry',
    fCategoria: 'Entry category', fExpediente: 'File number', fDisposicion: 'Ruling',
    fRadicacion: 'Residency date',
    fCalle: 'Street and number',
    fLocalidad: 'City',
    fProvincia: 'Province',
    fPais: 'Country',
    mrz_ilegible: 'We could not read the back. Take the photo straight, with good light and no glare, including the full bottom text band.',
    mrz_no_valida: 'We read the machine-readable band on the back, but its check digits do not add up. Another photo will not change that: either the document is a specimen or sample, or one digit was misread. It can be sent anyway.',
    dni_mismatch: 'The document number in the photo does not match the data on file.',
    birth_mismatch: 'The birth date in the photo does not match the data on file.',
    name_mismatch: 'The name in the photo does not match the data on file.',
    anverso_dni: 'The document number is not readable on the front. Try better lighting.',
    anverso_numero_ajeno: 'The photo is legible: the document says {leido} and the data on file says {suyo}. What does not match is the document, not the photo.',
    anverso_apellido: 'The last name is not readable on the front. Try better lighting.',
    anverso_otro_documento: 'The front and the back are not from the same document: the number read on the front is not the one on the back. Check that both photos are of the same ID.',
    anverso_sin_referencia: 'To check the front we need the back: upload it and we will verify. Having the data in the form works too.',
    calidad: 'Whoever receives this photo may struggle to read it:',
    calidadNoBloquea: 'It can be sent anyway: this is a warning, it does not block sending.',
    m_fuera_de_foco: 'it is out of focus — move the document a little further from the camera',
    m_reflejo: 'there is glare on the plastic — turn the document so the light does not hit it head on',
    m_muy_oscura: 'it is too dark — find more light',
    m_muy_chica: 'the document is too small in the frame — move it closer until it fills the frame',
    m_recortada: 'the document is cut off — fit the whole card in the frame',
    ocr_load_failed: 'Could not load the document reader. Check your connection and retry.',
    upload_failed: 'Could not send. Please retry in a few minutes.',
    e_data_mismatch: 'The stored data does not match the document. Fix it in the form —you can fill it from the ID right here— and confirm it before sending the photo.',
    e_mrz_invalid: 'The server could not read the band on the back. Take a sharper photo with the full band.',
    e_bad_image: 'The server rejected the images. Try other photos (JPG).',
    e_too_large: 'The photos are too large or too small. Take another one with the camera, uncropped.',
    e_rate_limited: 'Too many attempts in a row. Wait a few minutes and try again.',
    e_link: 'The validation link is no longer valid. Ask for a new one from whoever emailed you.',
    e_link_expired: 'This link has expired. Ask whoever emailed you for a new one.',
    image_error: 'Could not process the image. Try another photo (JPG).',
  },
  pt: {
    title: 'Foto do documento (frente e verso)',
    intro: 'Com estas fotos preenchemos os dados do autor e enviamos o documento ao servidor da SADAIC. São lidas no seu dispositivo: até tocar em "Enviar à SADAIC" não saem daqui.',
    anverso: 'Frente do documento',
    reverso: 'Verso do documento',
    elegir: 'Escolher foto',
    tomar: 'Tirar foto',
    camaraFrente: 'Tirar foto da frente do documento',
    camaraDorso: 'Tirar foto do verso do documento',
    procesando: 'Lendo a foto…',
    extrayendo: 'Lendo os dados do documento…',
    okAnverso: 'Frente verificada',
    cortePregunta: 'Esta foto parece trazer os DOIS lados do documento. Se for assim, separamos e preenchemos frente e verso de uma vez. A linha pontilhada mostra onde cortaríamos.',
    corteSi: 'Sim, separar os dois lados', corteNo: 'Não, é um lado só',
    noSalieron: 'Não saíram da frente:', loQueVioElOcr: 'Isto é o que o OCR leu, linha por linha:',
    reconocido: 'Reconhecemos na foto:', rNumero: 'N° do documento', rApellido: 'Sobrenome', rNombre: 'Nome', rNacimiento: 'Nascimento', rNada: 'Não conseguimos localizar nenhum dado na foto.',
    rSexo: 'Sexo', rNacionalidad: 'Nacionalidade', rVencimiento: 'Vencimento',
    okReverso: 'Verso verificado (número, nome e data coincidem)',
    avisoAlEnviar: 'Da foto da frente não conseguimos ler: {faltan}. Pode enviar assim mesmo — a SADAIC pede a foto, não que a máquina a leia: aperte de novo para enviar.',
    enviar: 'Enviar à SADAIC',
    cerrar: 'Fechar',
    enviado: 'Documento enviado com sucesso.',
    enviadoDetalle: 'Já pode fechar esta janela.',
    datosLeidos: 'Dados lidos do documento:',
    completar: 'Preencher o formulário com estes dados',
    copiar: 'Copiar', copiarDni: 'Copiar documento',
    fCuitCalculado: 'CUIT correspondente', calculado: 'calculado',
    completado: 'O formulário foi preenchido com os dados do documento. Revise antes de confirmar.',
    verificado: 'verificado',
    coinciden: '2 fontes coincidem',
    enConflicto: 'leituras diferentes',
    confirmado: 'confirmado na frente',
    sinVerificar: 'sem verificar',
    arreglaCompletando: 'Se os dados do formulário estão vazios ou errados, preencha-os com os do documento e a verificação é refeita sozinha.',
    fGenero: 'Gênero',
    fDocumento: 'N° do documento',
    fVencimiento: 'Validade',
    fNacionalidad: 'Nacionalidade',
    vencido: 'VENCIDO',
    avisoVencido: 'O documento está vencido. Pode ser enviado assim mesmo: a SADAIC pede a foto do documento, não que esteja vigente.',
    tramiteTapado: 'Cobrimos o número de Trámite e o código de barras, que o leva dentro. Os dados do código já foram lidos: o que se perde é que outra pessoa os leia da foto.',
    taparAuto: 'Cobrir automaticamente',
    taparAutoAyuda: 'Se precisar que algo coberto fique visível, desmarque. Também pode cobrir o que quiser arrastando um retângulo sobre a imagem.',
    fFiscal: 'CUIT/CUIL',
    fDomicilio: 'Endereço (conforme o documento)',
    domicilioNoLeido: 'Não foi possível ler do documento. Informe manualmente.',
    cuitOk: 'O CUIT registrado corresponde a este documento.',
    cuitSexo: 'O CUIT começa com um prefixo que não corresponde ao sexo do documento: 20 é de homens e 27 de mulheres. Pode estar errado o CUIT, o gênero registrado, ou a leitura do documento — o sexo é o único campo do MRZ sem dígito de controle.',
    incluirDomicilio: 'Preencher também o endereço',
    domicilioPuedeDiferir: 'O endereço do documento pode estar desatualizado. Desmarque se o real for outro.',
    fPaisNacimiento: 'País de nascimento', fIngreso: 'Data de entrada no país',
    fCategoria: 'Categoria de entrada', fExpediente: 'Processo', fDisposicion: 'Disposição',
    fRadicacion: 'Data de radicação',
    fCalle: 'Rua e número',
    fLocalidad: 'Cidade',
    fProvincia: 'Província',
    fPais: 'País',
    mrz_ilegible: 'Não foi possível ler o verso. Tire a foto reta, com boa luz e sem reflexos, incluindo a faixa de letras de baixo completa.',
    mrz_no_valida: 'Lemos a banda de letras do verso, mas seus dígitos de controle não fecham. Outra foto não vai mudar isso: ou o documento é uma amostra, ou um dígito foi lido errado. Pode ser enviada assim mesmo.',
    dni_mismatch: 'O número do documento da foto não coincide com os dados cadastrados.',
    birth_mismatch: 'A data de nascimento da foto não coincide com os dados cadastrados.',
    name_mismatch: 'O nome da foto não coincide com os dados cadastrados.',
    anverso_dni: 'O número do documento não é legível na frente. Tente com melhor luz.',
    anverso_numero_ajeno: 'A foto está legível: o documento diz {leido} e os dados cadastrados dizem {suyo}. O que não coincide é o documento, não a foto.',
    anverso_apellido: 'O sobrenome não é legível na frente. Tente com melhor luz.',
    anverso_otro_documento: 'A frente e o verso não são do mesmo documento: o número lido na frente não é o do verso. Verifique que as duas fotos sejam do mesmo documento.',
    anverso_sin_referencia: 'Para verificar a frente é preciso o verso: envie-o e verificamos. Ter os dados no formulário também serve.',
    calidad: 'Quem receber esta foto pode ter dificuldade para lê-la:',
    calidadNoBloquea: 'Pode ser enviada assim mesmo: isto é um aviso, não impede o envio.',
    m_fuera_de_foco: 'está fora de foco — afaste um pouco o documento da câmera',
    m_reflejo: 'há reflexo sobre o plástico — gire o documento para a luz não bater de frente',
    m_muy_oscura: 'está muito escura — procure mais luz',
    m_muy_chica: 'o documento ficou muito pequeno na foto — aproxime até preencher o quadro',
    m_recortada: 'o documento ficou cortado — enquadre o documento inteiro',
    ocr_load_failed: 'Não foi possível carregar o leitor de documentos. Verifique sua conexão.',
    upload_failed: 'Não foi possível enviar. Tente novamente em alguns minutos.',
    e_data_mismatch: 'Os dados salvos não coincidem com o documento. Corrija-os no formulário —pode preenchê-los a partir do documento, aqui mesmo— e confirme antes de enviar a foto.',
    e_mrz_invalid: 'O servidor não conseguiu ler a faixa do verso. Tire outra foto, mais nítida e com a faixa completa.',
    e_bad_image: 'O servidor recusou as imagens. Tente outras fotos (JPG).',
    e_too_large: 'As fotos são grandes ou pequenas demais. Tire outra com a câmera, sem recortar.',
    e_rate_limited: 'Tentativas demais seguidas. Espere alguns minutos e tente de novo.',
    e_link: 'O link de validação já não é válido. Peça um novo a quem lhe enviou o e-mail.',
    e_link_expired: 'Este link expirou. Peça um novo a quem lhe enviou o e-mail.',
    image_error: 'Não foi possível processar a imagem. Tente outra foto (JPG).',
  },
};

/** Los textos de un idioma; uno desconocido cae al castellano. */
export function textosDni(idioma: string): Record<string, string> {
  return TEXTOS_DNI[idioma as IdiomaDni] ?? TEXTOS_DNI.es;
}
